import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const auth = useAuth();

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    logger.info('🔄 Starting Supabase-only login process for:', formData.email);

    try {
      const result = await auth.login(formData.email, formData.password, formData.rememberMe);
      
      if (result.success) {
        logger.info('✅ Login successful, navigating to dashboard...');
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
      
    } catch (error) {
      logger.error('❌ Critical login error:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Cannot connect to authentication service. Please check your internet connection.';
      } else if (error.message.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError('');
    
    try {
      logger.info('🔄 Attempting Google OAuth with AuthContext...');
      
      // Use the AuthContext loginWithGoogle function
      const result = await auth.loginWithGoogle();

      if (!result.success) {
        logger.error('❌ Google OAuth error:', result.error);
        
        let errorMessage = 'Google sign-in failed. Please try again.';
        
        if (result.error.includes('popup')) {
          errorMessage = 'Popup blocked. Please allow popups for this site and try again.';
        } else if (result.error.includes('network')) {
          errorMessage = 'Network error during Google sign-in. Please check your connection.';
        } else if (result.error) {
          errorMessage = result.error;
        }
        
        setError(errorMessage);
        setIsGoogleLoading(false);
      }
      // If successful, the user will be redirected by Supabase OAuth flow

    } catch (error) {
      logger.error('❌ Google login error:', error);
      setError('Google authentication error. Please try again or use email/password login.');
      setIsGoogleLoading(false);
    }
  };

  const debugAuthContext = () => {
    logger.info('🔍 AuthContext Debug Info:', {
      isAuthenticated: auth.isAuthenticated,
      user: auth.user,
      loading: auth.loading,
      debugInfo: auth.getAuthDebugInfo()
    });
  };

  logger.info('NODE_ENV:', process.env.NODE_ENV);
  logger.info('ENV files:', process.env);

  return (
    <>
      <div className="min-h-screen bg-black flex relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute top-[10%] left-[10%] w-[300px] h-[300px] bg-gradient-to-br from-[#e07a5f] to-[#ff6b6b] rounded-full opacity-10 blur-[100px] animate-float" />
        <div className="absolute bottom-[10%] right-[10%] w-[250px] h-[250px] bg-gradient-to-br from-blue-500 to-purple-500 rounded-full opacity-10 blur-[80px] animate-float-reverse" />

        {/* Left Side - Branding (Hidden on mobile) */}
        <div className="hidden lg:flex flex-1 flex-col justify-center items-center p-8 z-10">
          <div className="text-center max-w-md">
            {/* Logo */}
            <Link to="/" className="inline-flex items-center gap-4 text-decoration-none mb-8">
              <span className="text-5xl">🕵️‍♂️</span>
              <span className="text-4xl font-bold text-[#e07a5f]">
                WhyDidItDie
              </span>
            </Link>

            {/* Tagline */}
            <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-white via-[#e07a5f] to-[#ff6b6b] bg-clip-text text-transparent leading-tight">
              Welcome Back, Detective
            </h1>
            
            <p className="text-lg text-white/70 mb-8 leading-relaxed">
              Ready to solve your next campaign mystery?
            </p>

            {/* Stats/Social Proof */}
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl font-bold text-[#e07a5f]">
                  $2.4M+
                </div>
                <div className="text-sm text-white/70">
                  Ad Spend Saved
                </div>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl font-bold text-[#e07a5f]">
                  10K+
                </div>
                <div className="text-sm text-white/70">
                  Campaigns Analyzed
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex flex-col justify-center items-center p-4 md:p-8 z-10">
          <div className="w-full max-w-md p-6 md:p-8 bg-white/5 rounded-3xl border border-white/10 backdrop-blur-2xl shadow-2xl">
            {/* Mobile Logo (only visible on mobile) */}
            <div className="block lg:hidden text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 text-decoration-none">
                <span className="text-3xl">🕵️‍♂️</span>
                <span className="text-2xl font-bold text-[#e07a5f]">
                  WhyDidItDie
                </span>
              </Link>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                Sign In
              </h2>
              <p className="text-white/70 text-base">
                Access your campaign forensics dashboard
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mb-6 text-red-500 text-sm break-words">
                {error}
              </div>
            )}

            {/* Development Debug Panel */}
            {/* {(process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') ? (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-6 text-blue-400 text-sm">
                <div className="flex justify-between items-center mb-2">
                  <strong>Dev Mode - 100% Supabase ✅</strong>
                  <button 
                    onClick={debugAuthContext}
                    className="bg-blue-500/20 border border-blue-500/50 text-blue-400 px-2 py-1 rounded text-xs hover:bg-blue-500/30 transition"
                  >
                    Debug Auth
                  </button>
                </div>
                <div>Direct Supabase authentication active.</div>
                <div className="text-xs opacity-80">No backend auth layer. Check console for logs.</div>
              </div>
            ): null} */}
            

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading || isLoading}
              className={`w-full flex items-center justify-center gap-3 p-4 bg-white text-gray-700 text-base font-semibold rounded-lg cursor-pointer transition-all duration-300 mb-6 shadow-lg
                ${isGoogleLoading || isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-xl'}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isGoogleLoading ? 'Signing in...' : 'Continue with Google'}
            </button>
            <p className="text-xs text-center text-white/50 mb-6">
            🔐 Securely powered by Supabase
            </p>

            {/* Divider */}
            <div className="flex items-center mb-6 gap-4">
              <div className="flex-1 h-px bg-white/20"></div>
              <span className="text-white/60 text-sm">or</span>
              <div className="flex-1 h-px bg-white/20"></div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Email Input */}
              <div>
                <label className="block mb-2 text-white/90 text-sm font-medium">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white text-base outline-none transition-all focus:border-[#e07a5f] focus:bg-white/15 placeholder:text-white/50"
                  placeholder="you@company.com"
                />
              </div>

              {/* Password Input */}
              <div>
                <label className="block mb-2 text-white/90 text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 pr-10 bg-white/10 border border-white/20 rounded-lg text-white text-base outline-none transition-all focus:border-[#e07a5f] focus:bg-white/15 placeholder:text-white/50"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none text-white/60 cursor-pointer text-sm"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Remember & Forgot */}
              <div className="flex justify-between items-center text-sm flex-wrap gap-2">
                <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="bg-white/10 border-white/30"
                  />
                  Remember me
                </label>
                <Link to="/forgot-password" className="text-[#e07a5f] no-underline text-sm">
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || isGoogleLoading}
                className={`w-full p-4 bg-[#e07a5f] border-none rounded-lg text-white text-base font-bold transition-all duration-300 shadow-lg
                  ${isLoading || isGoogleLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#cc6f53] hover:-translate-y-0.5 hover:shadow-xl'}`}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>

              {/* Meta Policy Compliance */}
              <p className="text-xs text-center text-gray-500 mt-2">
                By continuing, you agree to our <a href="https://app.whydiditdie.com/privacy" className="text-blue-500" target="_blank">Privacy Policy</a> and <a href="https://app.whydiditdie.com/terms" className="text-blue-500" target="_blank">Terms of Service</a>.
              </p>

              {/* Sign Up Link */}
              <div className="text-center pt-4 border-t border-white/10">
                <span className="text-white/70">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-[#e07a5f] no-underline font-bold">
                    Start free trial
                  </Link>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Tailwind Animation Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-reverse {
          animation: float 10s ease-in-out infinite reverse;
        }
      `}</style>
    </>
  );
};

export default Login;